import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSupabaseClient } from '../../data/supabase-client';
import { useCapturePhotos } from '../../data/queries/use-capture-photos';
import {
  createSupabasePhotoTransport,
  deliverPendingPhotos,
  stageForDelivery,
  type PhotoTransportPort,
} from '../../data/photos/photo-delivery';
import { MAX_SOURCE_BYTES, processPhoto } from '../../data/photos/photo-pipeline';
import { getPhotoStagingStore, type StagedPhoto } from '../../data/photos/photo-staging';
import { buildCapturePhotoViews, type CapturePhotoView } from '../../data/photos/photo-view';
import { selectImageRaster } from '../../platform/image-raster';
import { selectPhotoSource, type PhotoSourceKind } from '../../platform/photo-source';
import { removeCapturePhoto, reclassifyCapturePhoto } from '../../data/writers/photo-writers';
import { sessionOwnerUserId, useOptionalSession } from '../auth/session';
import { reportError } from '../../lib/monitoring';
import type { PhotoClass } from '../../data/database.types';
import {
  describeLimitRefusal,
  describePipelineRefusal,
  describeStagingFailure,
} from './photo-messages';

/**
 * THE PHOTO BOARD — everything the Capture's photo section does (M5 / S05-04).
 *
 * This hook is the wiring, and the wiring is the whole point of the slice: the
 * pipeline (S05-02) and the durable queue (S05-03) were both built and proven
 * with nothing calling them. Here is the one call chain, and there is no other:
 *
 *   pick → processPhoto → stageForDelivery → deliverPendingPhotos → Supabase
 *
 * **NO ALTERNATE UPLOAD PATH EXISTS, and that is enforced rather than
 * intended.** This hook never touches Storage, never calls `attach_capture_photo`,
 * and never persists a photo itself — a source guard in
 * `tests/unit/photo-upload-boundary.test.ts` fails if the feature tree ever
 * grows one. `Retry` is not a second route either: it re-enters the SAME queue,
 * which resumes at the exact stage that failed with the same object paths and
 * the same `client_photo_uuid`, so a retry replaces rather than duplicates.
 *
 * **THE UNSANITIZED ORIGINAL NEVER LEAVES THIS FUNCTION'S STACK.** `pick`
 * returns untrusted bytes; the only thing done with them is to hand them to
 * `processPhoto`, whose branded `VerifiedPhoto` is the only thing the staging
 * store will accept. That is a compile-time boundary, not a discipline.
 *
 * **PHOTOS ARE PROCESSED ONE AT A TIME**, including a twelve-photo
 * multi-select — the direction R1 gave in S05-02 and recorded in I-042.
 * Decoding twelve 12-megapixel images at once on a mid-range phone is exactly
 * the thing that looks free until it is not.
 *
 * **NO STATUS IS TOUCHED.** Nothing here writes a Capture or Project status,
 * and nothing here reads one to decide whether a photo may be added: a CLOSED
 * Project still allows photo work on an existing Capture (D-029).
 */

export interface CapturePhotoBoard {
  readonly views: CapturePhotoView[];
  readonly loading: boolean;
  /** A photo is being acquired, processed or staged right now. */
  readonly adding: boolean;
  /**
   * A photo is IN FLIGHT — reported by the queue itself, not inferred.
   *
   * The queue is sequential, so this is true of at most one photo per owner,
   * and it is exactly the photo whose tile says "Uploading…".
   */
  readonly delivering: boolean;
  readonly loadError: string | null;
  /** Refusals and failures from the last action. Cleared when a new one starts. */
  readonly notices: readonly string[];
  readonly dismissNotices: () => void;
  readonly addPhotos: (kind: PhotoSourceKind, photoClass: PhotoClass) => Promise<void>;
  readonly retryDelivery: () => Promise<void>;
  readonly reclassify: (view: CapturePhotoView, photoClass: PhotoClass) => Promise<void>;
  readonly remove: (view: CapturePhotoView) => Promise<void>;
  /**
   * **DISCARD ONE STAGED PHOTO THAT NEVER REACHED THE SERVER (H-4, I-044).**
   *
   * The only photo state that previously had no way out: a photo failing
   * before attachment kept its slot and its bytes, and the sole escape was an
   * explicit sign-out — which takes every other staged photo with it.
   *
   * Deliberately narrow. It refuses anything with a server row, so a
   * canonical photo can never be destroyed through this path; removal of an
   * attached photo remains the non-destructive server writer (PT §9).
   */
  readonly discardStaged: (view: CapturePhotoView) => Promise<void>;
}

export function useCapturePhotoBoard(captureId: string | undefined): CapturePhotoBoard {
  const session = useOptionalSession();
  const ownerUserId = sessionOwnerUserId(session);
  const client = getSupabaseClient();

  const [source] = useState(() => selectPhotoSource());
  const [raster] = useState(() => selectImageRaster());
  const [staging] = useState(() => getPhotoStagingStore());
  const transport = useMemo<PhotoTransportPort | null>(
    () => (client ? createSupabasePhotoTransport(client) : null),
    [client],
  );

  const { status, photos, error: loadError, reload } = useCapturePhotos(captureId);
  const [staged, setStaged] = useState<StagedPhoto[]>([]);
  const [activeUuid, setActiveUuid] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [notices, setNotices] = useState<readonly string[]>([]);

  // Every async step below can finish after the screen is gone — Brandon taps
  // a photo and immediately goes back. Setting state then is a React warning
  // and, worse, a write into a screen nobody is looking at.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * NO IDENTITY, NO READ — the fail-closed rule the draft and audio stores
   * already follow. After an explicit sign-out this is what empties the board,
   * so purged photos cannot linger on screen as if they still existed.
   */
  const readStaged = useCallback(async (): Promise<StagedPhoto[]> => {
    if (!ownerUserId) return [];
    try {
      return await staging.list(ownerUserId);
    } catch (error) {
      reportError(error, 'photos.listStaged');
      return [];
    }
  }, [ownerUserId, staging]);

  const refreshStaged = useCallback(async () => {
    const records = await readStaged();
    if (alive.current) setStaged(records);
  }, [readStaged]);

  useEffect(() => {
    let cancelled = false;
    void readStaged().then((records) => {
      if (!cancelled) setStaged(records);
    });
    return () => {
      cancelled = true;
    };
  }, [readStaged]);

  /**
   * Run the durable queue. Called on mount as well as after adding and on
   * Retry — mounting the screen after a relaunch or a process death is exactly
   * when the queue must pick up where the dead process stopped, and it does
   * that by reading the durable store rather than any memory of what happened.
   */
  const runDelivery = useCallback(async () => {
    if (!ownerUserId || !transport) return;
    try {
      // "A photo is in flight" is reported BY THE QUEUE, not guessed around
      // it. `onIdle` runs in the queue's own `finally`, so the screen cannot
      // be left saying something is uploading after the pass has ended.
      const result = await deliverPendingPhotos(staging, transport, ownerUserId, {
        onPhotoStart: (uuid) => {
          if (alive.current) setActiveUuid(uuid);
        },
        onIdle: () => {
          if (alive.current) setActiveUuid(null);
        },
      });
      // Server rows change as photos attach and confirm, and local records
      // disappear as they are cleaned up. Both halves are re-read from their
      // own source of truth rather than patched from the outcomes.
      await refreshStaged();
      await reload();
      const failures = result.outcomes
        .filter((outcome) => outcome.failure !== null)
        .map((outcome) => outcome.failure)
        .filter((failure): failure is string => failure !== null);
      // Failures are already visible per photo. One line is added only when
      // something failed, so the section does not shout after a clean pass.
      if (failures.length > 0 && alive.current) {
        setNotices((current) => [...current, ...uniqueFailureLines(failures)]);
      }
    } catch (error) {
      reportError(error, 'photos.deliver');
      if (alive.current) setActiveUuid(null);
    }
  }, [ownerUserId, transport, staging, refreshStaged, reload]);

  /**
   * ON MOUNT, AS WELL AS AFTER ADDING AND ON RETRY.
   *
   * Opening the Capture after a relaunch — or after Android killed the process
   * mid-upload — is exactly when the queue has to pick up where the dead
   * process stopped, and it does that by reading the DURABLE store rather than
   * any memory of what was happening.
   */
  useEffect(() => {
    void runDelivery();
  }, [runDelivery]);

  const addPhotos = useCallback(
    async (kind: PhotoSourceKind, photoClass: PhotoClass) => {
      if (!ownerUserId || !captureId || !transport) return;
      if (alive.current) {
        setNotices([]);
        setAdding(true);
      }
      const problems: string[] = [];
      try {
        // Multi-select on the library path only: a camera returns one photo,
        // and asking for `multiple` there confuses pickers that then ignore
        // the capture hint entirely.
        const acquired = await source.pick(kind, { multiple: kind === 'library' });
        // An empty result is a CANCEL, not an error, and must not be reported
        // as one (the port's own contract).
        for (const item of acquired) {
          const processed = await processPhoto(item.bytes, item.declaredMime, raster);
          if (!processed.ok) {
            problems.push(describePipelineRefusal(processed, item.name));
            continue;
          }
          const admitted = await stageForDelivery({
            staging,
            ownerUserId,
            captureId,
            photoClass,
            photo: processed.photo,
            maxSourceBytes: MAX_SOURCE_BYTES,
            nowMs: Date.now(),
          });
          if (!admitted.ok) {
            problems.push(
              admitted.code === 'staging-failed'
                ? describeStagingFailure()
                : describeLimitRefusal(admitted.code),
            );
          }
        }
      } catch (error) {
        reportError(error, 'photos.add');
        problems.push('That photo could not be added. Please try again.');
      } finally {
        if (alive.current) {
          setAdding(false);
          setNotices(problems);
        }
      }
      await refreshStaged();
      await runDelivery();
    },
    [ownerUserId, captureId, transport, source, raster, staging, refreshStaged, runDelivery],
  );

  const reclassify = useCallback(
    async (view: CapturePhotoView, photoClass: PhotoClass) => {
      // Reclassification acts on the SERVER row (PT §9's Work Log record), so
      // a photo that has not attached yet has nothing to reclassify. The
      // screen does not offer it in that state; this is the matching guard.
      if (!client || view.serverPhotoId === null) return;
      const result = await reclassifyCapturePhoto(client, view.serverPhotoId, photoClass);
      if (!result.ok && alive.current) setNotices([result.message]);
      await reload();
    },
    [client, reload],
  );

  const remove = useCallback(
    async (view: CapturePhotoView) => {
      if (!client || view.serverPhotoId === null) return;
      const result = await removeCapturePhoto(client, view.serverPhotoId);
      if (!result.ok && alive.current) setNotices([result.message]);
      // The row and its objects survive (PT §9); the active list simply stops
      // matching it, because `use-capture-photos` filters `removed_at is null`.
      await reload();
    },
    [client, reload],
  );

  /**
   * Permanently forget ONE staged photo. Local only, and irreversible.
   *
   * **THE SERVER-ROW CHECK IS THE SAFETY RAIL, NOT THE UI.** A view carrying a
   * `serverPhotoId` is a real `capture_photo` row and is refused here even if
   * a caller asks — the confirmation dialog can be changed by anyone editing a
   * component, while this cannot.
   */
  const discardStaged = useCallback(
    async (view: CapturePhotoView) => {
      if (ownerUserId === null) return;
      // THREE conditions, all required. `serverPhotoId` refuses a canonical
      // photo — those are removed non-destructively through the writer and keep
      // their row and history (PT §9). `staged` refuses anything with no local
      // bytes to forget, so this can never be a no-op that looks like a
      // deletion. `key` IS the `client_photo_uuid`.
      //
      // **THE THIRD IS THE DELIVERY QUEUE (H-4 correction 2).** Discarding
      // while a pass is in flight is refused outright, for two distinct
      // reasons that a per-photo check would each miss:
      //
      //   1. The photo being retried RIGHT NOW does not read 'uploading'.
      //      `stateForStaged` tests `lastFailure` first, and nothing clears it
      //      when an attempt starts — so a failing photo mid-retry still reads
      //      'failed'. That is the single most likely moment for someone to
      //      reach for this control, because the escape hatch exists FOR
      //      repeatedly-failing photos.
      //   2. A merely QUEUED photo is not safe either. `removeStaged` does not
      //      bump the purge epoch, so a pass that already read its snapshot can
      //      still persist the record after this deletes it — the photo comes
      //      back. Only `purge` closes that window, and sign-out owns it.
      //
      // So the rail is the pass, not the photo. It is deliberately coarser
      // than necessary: waiting out a short pass costs a moment, while either
      // race above costs a photo or resurrects a deleted one.
      if (view.serverPhotoId !== null || view.staged === null) return;
      if (activeUuid !== null) return;

      await staging.removeStaged(ownerUserId, view.key);
      // Re-reading staged truth is what releases the count slot and the byte
      // budget: both are derived from the store, never tracked separately.
      await refreshStaged();
    },
    [ownerUserId, staging, refreshStaged, activeUuid],
  );

  const views = useMemo(
    () =>
      buildCapturePhotoViews({
        captureId: captureId ?? '',
        rows: photos,
        staged,
        activeClientPhotoUuid: activeUuid,
      }),
    [captureId, photos, staged, activeUuid],
  );

  return {
    views,
    loading: status === 'loading',
    adding,
    delivering: activeUuid !== null,
    loadError: status === 'failed' ? loadError : null,
    notices,
    dismissNotices: useCallback(() => {
      setNotices([]);
    }, []),
    addPhotos,
    retryDelivery: runDelivery,
    reclassify,
    remove,
    discardStaged,
  };
}

/**
 * Transport failures repeated across photos collapse to one line.
 *
 * Twelve photos failing on one dead connection is ONE fact about the
 * connection, and printing it twelve times buries the per-photo states that
 * actually differ.
 */
function uniqueFailureLines(failures: readonly string[]): string[] {
  return [...new Set(failures)].map((failure) => `Upload did not finish: ${failure}`);
}
